'use client';

import { getStoryblokApi, storyblokEditable } from '@storyblok/react';
import { FC, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import ButtonArrow, {
  ButtonDirection,
} from '@/components/elements/ButtonArrow';
import Container from '@/components/elements/Container';
import Events from '@/components/elements/Features/Events';
import Project from '@/components/elements/Features/Projects';
import Spotlight from '@/components/elements/Features/Spotlights';
import ItemSlider from '@/components/elements/ItemSlider';
import Section from '@/components/elements/Section';
import StandardText, {
  StandardTextSize,
} from '@/components/elements/StandardText';

type ItemSliderProps = {
  blok: any;
  className: string;
};

const FEATURED_COMPONENTS = {
  spotlight: Spotlight,
  project: Project,
  events: Events,
};

async function getFeatured(features: string[]) {
  const storyblokApi = getStoryblokApi();
  const { data } = await storyblokApi.get(`cdn/stories`, {
    version:
      process.env.NEXT_PUBLIC_STORYBLOK_VERSION === 'published'
        ? 'published'
        : 'draft',
    by_uuids: features.join(','),
  });
  return data.stories;
}

const SliderSection: FC<ItemSliderProps> = ({ blok, className }) => {
  const [index, setIndex] = useState(0);
  const interval = useRef<any>();
  const container = useRef<HTMLDivElement>();
  const [width, setWidth] = useState(0);
  const [itemWidth, setItemWidth] = useState(0);
  const [featured, setFeatured] = useState<any[]>([]);

  useEffect(() => {
    getFeatured(blok.featured).then((data) => setFeatured(data));
  }, [blok.featured]);

  function move(dir = 0) {
    setIndex(
      (prev) => (blok.featured.length + prev + dir) % blok.featured.length
    );
  }

  useEffect(() => {
    if (blok.autoAdvance) {
      interval.current = setInterval(() => move(1), blok.delay * 1000);
    }
    return () => {
      clearInterval(interval.current);
    };
  }, []);

  useEffect(() => {
    if (window.innerWidth > 640) {
      setItemWidth(width / 2 + 32);
    } else {
      setItemWidth(width);
    }
  }, [width]);

  useEffect(() => {
    function update() {
      if (container.current) {
        const size = container.current.getBoundingClientRect();
        setWidth(size.width);
      }
    }

    update();
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('resize', update);
    };
  }, []);

  if (!blok.featured.length) return null;

  return (
    <Section
      gapTop="2xs"
      {...storyblokEditable(blok)}
      className={twMerge('overflow-hidden', className)}
    >
      <Container size="wider">
        <div className="flex flex-col gap-8 md:gap-12">
          <div className="max-w-2xl">
            <StandardText blok={blok} size={StandardTextSize.MD} />
          </div>
          <div className="grid grid-cols-[1fr_auto] place-content-center items-center gap-8">
            <div className="h-px bg-theme-lightgrey"></div>
            <div className="grid grid-cols-2 gap-6">
              <button
                className="size-[40px] cursor-pointer rounded-full border border-solid border-theme-primary p-[5px]"
                onClick={() => move(-1)}
              >
                <ButtonArrow
                  className="m-1 fill-theme-primary"
                  direction={ButtonDirection.LEFT}
                />
              </button>
              <button
                className="size-[40px] cursor-pointer rounded-full border border-solid border-theme-primary p-[5px]"
                onClick={() => move(1)}
              >
                <ButtonArrow
                  className="m-1 fill-theme-primary"
                  direction={ButtonDirection.RIGHT}
                />
              </button>
            </div>
          </div>
          <div className="overflow-hidden" ref={container}>
            <ItemSlider index={index} itemWidth={itemWidth - 16} itemGap={32}>
              {featured.map((s) => {
                if (!s.content) return null;
                const Comp = FEATURED_COMPONENTS[s.content.component];

                if (!s.published_at) return null;
                return <Comp key={s.id} {...s} {...s.content} />;
              })}
            </ItemSlider>
          </div>
        </div>
      </Container>
    </Section>
  );
};

export default SliderSection;
